import React, { useState } from 'react';
import Bar from '../../../../Charts/Bar';

import styles from './Slider.module.css';

export const getAverageRating = inputData => {
  const valueArray = inputData.map(d => d.y * d.x);
  const numberOfResults = inputData.reduce((total, v) => total + v.y, 0);
  return valueArray.reduce((p, c) => p + c, 0) / numberOfResults;
};

const getMinMaxValue = arr => {
  if (!Array.isArray(arr) || arr.length === 0) {
    return { min: null, max: null };
  }

  // Filter only items where y > 0 and x is a valid number
  const xValues = arr
    .filter(item => item.y > 0 && typeof item.x === 'number')
    .map(item => item.x);

  if (xValues.length === 0) {
    return { min: null, max: null };
  }

  return {
    min: Math.min(...xValues),
    max: Math.max(...xValues)
  };
};

const Slider = props => {
  const {
    parent,
    data,
    start,
    end,
    onChartClick,
    filterCollectDataGroup,
    unfilteredData,
    block,
    sorting,
    setSorting,
    sortingOptions,
    activeFilters,
    responses,
    extremeUsersFilterLabels
  } = props;

  const [displayRatingPercentage, setDisplayRatingPercentage] = useState(true);
  const [displayStandardGraph, setDisplayStandardGraph] = useState(true);
  const [displayDropdown, setDisplayDropdown] = useState(false);

  const getRangeBar = () => {
    const createRangeBuckets = (dataInput, dataRanges) => {
      const result = [];

      dataRanges.forEach((range, index) => {
        const items = dataInput.filter(
          item => item.x >= range.minValue && item.x <= range.maxValue
        );

        const totalY = items.reduce((sum, item) => sum + item.y, 0);

        result.push({
          x: index + 1,
          y: totalY,
          xLabel: range.label
        });
      });

      return result;
    };

    const processedData =
      data && createRangeBuckets(data, block.sliderQuestionRanges);

    const processedUnfilteredData =
      unfilteredData &&
      createRangeBuckets(unfilteredData, block.sliderQuestionRanges);

    const onRangeClick = (blockId, property, index) => {
      const range = block.sliderQuestionRanges[index - 1];

      onChartClick(
        block.id,
        null,
        `Q${block.formattedGlobalIndex} (range: ${range.label})`,
        null,
        result => {
          if (
            result &&
            result.attributes &&
            result.attributes.block_results &&
            result.attributes.block_results.length
          ) {
            const questionResult = result.attributes.block_results.find(
              bR => bR && block && bR.block === block.id
            );

            if (
              questionResult &&
              (questionResult[property] || questionResult[property] === 0) &&
              questionResult[property] >= range.minValue &&
              questionResult[property] <= range.maxValue
            ) {
              return true;
            }
          }
          return false;
        },
        `${index - 1}`,
        true
      );
    };

    return (
      <Bar
        parent={parent}
        data={processedData}
        unfilteredData={processedUnfilteredData}
        color="#5200F1"
        marginLeft="110"
        height="200"
        tickFrequency="1"
        start={1}
        end={processedData.length}
        onChartClick={(blockId, property, index) =>
          onRangeClick(blockId, property, index)
        }
        filterCollectDataGroup={filterCollectDataGroup}
        showCounts
        displayRatingPercentage={displayRatingPercentage}
        numberOfResults={data.reduce((total, v) => total + v.y, 0)}
        unfilteredNumberOfResults={
          unfilteredData &&
          unfilteredData.reduce((total, v) => total + v.y, 0) > 0
            ? unfilteredData.reduce((total, v) => total + v.y, 0)
            : 1
        }
        block={block}
        activeFilteringOnThisChart={
          !!(
            activeFilters &&
            activeFilters.length &&
            activeFilters.some(
              activeFilter => activeFilter.filterName === block.id
            )
          )
        }
        showXLabels
      />
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.switchContainer}>
        <div className={styles.sortContainer}>
          <div
            role="presentation"
            className={styles.dropdownContainer}
            onClick={() => setDisplayDropdown(prev => !prev)}
          >
            {sorting}
            {displayDropdown && (
              <div className={styles.dropdownList}>
                {Object.values(sortingOptions).map(o => (
                  <span
                    role="presentation"
                    className={styles.dropdownOption}
                    key={o}
                    onClick={() => setSorting(o)}
                  >
                    {o}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
        <span role="presentation">
          {block &&
          block.sliderQuestionRanges &&
          block.sliderQuestionRanges.length ? (
            <label
              style={{ marginRight: 15 }}
              className={styles.ratingSwitchExtended}
              htmlFor="checkbox"
            >
              <input
                type="checkbox"
                checked={displayStandardGraph}
                className={styles.input}
              />

              <span
                className={styles.ratingSliderExtended}
                role="presentation"
                onClick={e => {
                  setDisplayStandardGraph(!displayStandardGraph);
                  e.stopPropagation();
                }}
              >
                <span
                  className={
                    displayStandardGraph
                      ? styles.ratingPercentageOn
                      : styles.ratingPercentageOff
                  }
                >
                  Default
                </span>
                <span
                  className={
                    !displayStandardGraph
                      ? styles.ratingNumberOn
                      : styles.ratingNumberOff
                  }
                >
                  Ranges
                </span>
              </span>
            </label>
          ) : null}

          <label className={styles.ratingSwitch} htmlFor="checkbox">
            <input
              type="checkbox"
              checked={displayRatingPercentage}
              className={styles.input}
            />

            <span
              className={styles.ratingSlider}
              role="presentation"
              onClick={e => {
                setDisplayRatingPercentage(!displayRatingPercentage);
                e.stopPropagation();
              }}
            >
              <span
                className={
                  displayRatingPercentage
                    ? styles.ratingPercentageOn
                    : styles.ratingPercentageOff
                }
              >
                %
              </span>
              <span
                className={
                  !displayRatingPercentage
                    ? styles.ratingNumberOn
                    : styles.ratingNumberOff
                }
              >
                #
              </span>
            </span>
          </label>
        </span>
      </div>
      {displayStandardGraph ? (
        <Bar
          parent={parent}
          data={data}
          unfilteredData={unfilteredData}
          color="#5200F1"
          marginLeft="110"
          height="200"
          tickFrequency="1"
          start={start}
          end={end}
          onChartClick={onChartClick}
          filterCollectDataGroup={filterCollectDataGroup}
          showCounts
          displayRatingPercentage={displayRatingPercentage}
          numberOfResults={data.reduce((total, v) => total + v.y, 0)}
          unfilteredNumberOfResults={
            unfilteredData &&
            unfilteredData.reduce((total, v) => total + v.y, 0) > 0
              ? unfilteredData.reduce((total, v) => total + v.y, 0)
              : 1
          }
          block={block}
          activeFilteringOnThisChart={
            !!(
              activeFilters &&
              activeFilters.length &&
              activeFilters.some(
                activeFilter => activeFilter.filterName === block.id
              )
            )
          }
        />
      ) : (
        getRangeBar()
      )}
      <div className={styles.additionalDetailsContainer}>
        <div className={styles.additionalDetailsContentContainer}>
          <div
            className={styles.bullet}
            style={{ backgroundColor: '#5300f2' }}
          />
          Average rating:{' '}
          <span
            className={styles.averageRatingCount}
            style={{ marginRight: '30px' }}
          >
            {getAverageRating(
              data.filter(d => !(block && block.allowSkip && d.x < 0))
            ).toFixed(2)}
          </span>
          Minimum value:{' '}
          <span
            className={styles.averageRatingCount}
            style={{ marginRight: '30px' }}
          >
            {
              getMinMaxValue(
                data.filter(d => !(block && block.allowSkip && d.x < 0))
              ).min
            }
          </span>
          Maximum value:{' '}
          <span
            className={styles.averageRatingCount}
            style={{ marginRight: '30px' }}
          >
            {
              getMinMaxValue(
                data.filter(d => !(block && block.allowSkip && d.x < 0))
              ).max
            }
          </span>
          {activeFilters && activeFilters.length ? (
            <div>
              <div
                className={styles.bullet}
                style={{ backgroundColor: '#8B8B8D' }}
              />
              Average rating:{' '}
              <span
                className={styles.averageRatingCount}
                style={{ marginRight: '30px' }}
              >
                {getAverageRating(
                  unfilteredData.filter(
                    d => !(block && block.allowSkip && d.x < 0)
                  )
                ).toFixed(2)}
              </span>
              Minimum rating:{' '}
              <span
                className={styles.averageRatingCount}
                style={{ marginRight: '30px' }}
              >
                {
                  getMinMaxValue(
                    unfilteredData.filter(
                      d => !(block && block.allowSkip && d.x < 0)
                    )
                  ).min
                }
              </span>
              Maximum value{' '}
              <span
                className={styles.averageRatingCount}
                style={{ marginRight: '30px' }}
              >
                {
                  getMinMaxValue(
                    unfilteredData.filter(
                      d => !(block && block.allowSkip && d.x < 0)
                    )
                  ).max
                }
              </span>
            </div>
          ) : null}
        </div>
      </div>
      {responses < 75 &&
      !(
        activeFilters &&
        activeFilters.length &&
        activeFilters.some(
          activeFilter =>
            activeFilter.value &&
            extremeUsersFilterLabels.includes(activeFilter.value)
        )
      ) ? (
        <div className={styles.disclaimer}>
          <div>
            Disclaimer: The{' '}
            {activeFilters && activeFilters.length ? 'selected' : 'total'}{' '}
            sample is rather small
            {` (< 75 respondents) `} which means that results are indicative but
            might not be statistically relevant.
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Slider;
